<template>
    <div class="fit">
        <MobileView v-if="isMobileView" :total="total">
            <template #panel>
                <div v-if="$slots.filter" class="row fit flex-grow items-center">
                    <slot name="filter" :is-mobile="isMobileView" />
                </div>
            </template>
            <template v-if="$slots.banner" #banner>
                <slot name="banner" />
            </template>
            <div
                v-if="$slots.count || total"
                class="q-mt-sm slot-count"
                :class="{ 'q-px-lg': isLargerThanMedium, 'q-px-md': !isLargerThanMedium }"
            >
                <template v-if="$slots.count">
                    <slot name="count" />
                </template>
                <template v-else>
                    {{ countText }}
                </template>
            </div>
            <template #content>
                <template v-if="!loading && noResults && $slots['no-results']">
                    <slot name="no-results" />
                </template>
                <ErrorAndLoadingFragment
                    v-else
                    :loading="loading"
                    :error="error"
                    :no-results="noResults"
                    :no-result-icon="noResultIcon"
                    :no-results-error-message="noResultsErrorMessage"
                />
                <template v-if="!loading && !error && !noResults">
                    <div class="slot-content col fit q-pa-sm">
                        <slot name="content" :is-mobile="isMobileView" />
                    </div>
                </template>
            </template>
        </MobileView>
        <!-- Desktop View -->
        <div
            v-else
            :class="[
                { 'small-content-container': !largeContentContainer, 'large-content-container': largeContentContainer },
                'desktop-container column full-height no-wrap',
            ]"
        >
            <div
                v-if="$slots.filter"
                class="flex-container flex-row justify-between items-center col-auto"
                :class="{ 'q-px-lg': isLargerThanMedium, 'q-px-md': !isLargerThanMedium }"
            >
                <slot name="filter" :is-mobile="isMobileView" />
            </div>
            <div
                v-if="$slots.count || total"
                class="q-mt-sm slot-count"
                :class="{ 'q-px-lg': isLargerThanMedium, 'q-px-md': !isLargerThanMedium }"
            >
                <template v-if="$slots.count">
                    <slot name="count" />
                </template>
                <template v-else>
                    {{ countText }}
                </template>
            </div>
            <div v-if="$slots.banner" class="q-mx-md q-mt-md" :class="{ 'q-mx-lg': isLargerThanMedium }">
                <slot name="banner" />
            </div>
            <template v-if="!loading && noResults && $slots['no-results']">
                <slot name="no-results" />
            </template>
            <ErrorAndLoadingFragment
                v-else
                :loading="loading"
                :error="error"
                :no-results="noResults"
                :no-result-icon="noResultIcon"
                :no-results-error-message="noResultsErrorMessage"
            />
            <div
                v-if="!loading && !error && !noResults"
                class="slot-content col fit q-pb-md q-pt-sm"
                :class="{ 'q-px-lg': isLargerThanMedium, 'q-px-md': !isLargerThanMedium }"
            >
                <slot name="content" :is-mobile="isMobileView" />
            </div>
        </div>
        <q-resize-observer @resize="onResize" />
    </div>
</template>

<script lang="ts" setup>
import { computed, ref, toRefs } from "vue";
import { ApolloError } from "@apollo/client/errors";
import MobileView from "./mobile-view.vue";
import ErrorAndLoadingFragment from "./error-and-loading-fragment.vue";
import { useScreenSize } from "@/shared/screen/composables/screen-size";
import { generateSearchHitsText } from "@/shared/utils/total-formatter.ts";
import { Total } from "@/shared/search/search.types.ts";
import { useQuasar } from "quasar";

const props = withDefaults(
    defineProps<{
        noResults?: boolean;
        noResultsErrorMessage?: string;
        loading?: boolean;
        error?: ApolloError | null;
        largeContentContainer?: boolean;
        noResultIcon?: string;
        total?: Total;
    }>(),
    {
        noResults: false,
        loading: false,
        largeContentContainer: false,
        noResultIcon: "fa-thin fa-cloud-question icon-light-color fa-4x",
    }
);

const { noResults, loading, total } = toRefs(props);
const { screen } = useQuasar();

const { isLargerThanMedium } = useScreenSize();

const isMobileView = ref(false);

const onResize = (value: { width: number }) => {
    isMobileView.value = value.width < screen.sizes.sm;
};

const countText = computed(() => {
    return total.value ? generateSearchHitsText(total.value) : "";
});
</script>

<style lang="scss" scoped>
.desktop-container {
    padding-top: $spacing-l;

    &:deep(.facets-filter-container) {
        overflow-y: auto;
        max-height: 32vh;
    }

    .non-content-element {
        padding: 0 $spacing-l;

        .screen--xs & {
            padding: 0 $spacing-m;
        }
    }
}
.slot-count,
:deep(.slot-count) {
    min-height: 17px;
    max-height: 17px;
    color: $grey-40;
    font-size: $smaller-font-size;
}
</style>
